import { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Input, Select, Space, Tag, message, Popconfirm } from 'antd';
import { EditOutlined, StopOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { getUsers, updateUser } from '../lib/api';
import { getUserDisplayName } from '../lib/user';
import type { ColumnType } from 'antd/es/table';

interface User {
  id: number;
  name: string | null;
  username: string | null;
  email: string | null;
  role: 'boss' | 'manager' | 'employee';
  managerId: number | null;
  isActive: boolean;
  createdAt: string;
  manager?: {
    id: number;
    name: string | null;
    username: string | null;
  } | null;
}

const { Option } = Select;

export default function UserManagement() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [form] = Form.useForm();

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const data = await getUsers();
      setUsers(data);
    } catch (error) {
      message.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'boss':
        return '老闆';
      case 'manager':
        return '主管';
      case 'employee':
        return '員工';
      default:
        return role;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'boss':
        return 'red';
      case 'manager':
        return 'blue';
      case 'employee':
        return 'default';
      default:
        return 'default';
    }
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    form.setFieldsValue({
      name: user.name,
      email: user.email,
      username: user.username,
      role: user.role,
      managerId: user.managerId,
    });
    setEditModalVisible(true);
  };

  const handleEditSubmit = async (values: any) => {
    if (!editingUser) return;

    try {
      await updateUser(editingUser.id, values);
      message.success('User updated successfully');
      setEditModalVisible(false);
      fetchUsers();
    } catch (error: any) {
      message.error(error.message || 'Failed to update user');
    }
  };

  const handleToggleActive = async (user: User) => {
    try {
      await updateUser(user.id, { isActive: !user.isActive });
      message.success(user.isActive ? 'User deactivated' : 'User activated');
      fetchUsers();
    } catch (error: any) {
      message.error(error.message || 'Failed to update user status');
    }
  };

  const columns: ColumnType<User>[] = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 60,
      responsive: ['lg'],
    },
    {
      title: '姓名',
      fixed: 'left',
      width: 120,
      render: (_, user) => getUserDisplayName(user),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      responsive: ['md'],
      render: (email) => email || '-',
    },
    {
      title: '帳號',
      dataIndex: 'username',
      responsive: ['lg'],
      render: (username) => username || '-',
    },
    {
      title: '角色',
      dataIndex: 'role',
      width: 90,
      render: (role) => (
        <Tag color={getRoleColor(role)}>{getRoleLabel(role)}</Tag>
      ),
    },
    {
      title: '直屬主管',
      responsive: ['md'],
      render: (_, user) =>
        user.manager ? getUserDisplayName(user.manager) : '-',
    },
    {
      title: '狀態',
      dataIndex: 'isActive',
      width: 80,
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? '啟用' : '停用'}
        </Tag>
      ),
    },
    {
      title: '操作',
      fixed: 'right',
      width: 150,
      render: (_, user) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(user)}
            size="small"
          >
            編輯
          </Button>
          <Popconfirm
            title={user.isActive ? '確認停用帳號？' : '確認啟用帳號？'}
            description={
              user.isActive
                ? '停用後該使用者將無法登入系統'
                : '啟用後該使用者將可以登入系統'
            }
            onConfirm={() => handleToggleActive(user)}
            okText="確認"
            cancelText="取消"
          >
            <Button
              icon={user.isActive ? <StopOutlined /> : <CheckCircleOutlined />}
              danger={user.isActive}
              type={user.isActive ? 'default' : 'primary'}
              size="small"
            >
              {user.isActive ? '停用' : '啟用'}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Filter out inactive users for manager selection (can't assign to inactive managers)
  const activeUsers = users.filter((u) => u.isActive);
  const potentialManagers = activeUsers.filter(
    (u) => u.role === 'boss' || u.role === 'manager'
  );

  return (
    <Card title="使用者管理">
      <Table
        columns={columns}
        dataSource={users}
        loading={loading}
        rowKey="id"
        scroll={{ x: 'max-content' }}
        pagination={{ pageSize: 10 }}
      />

      <Modal
        title="編輯使用者"
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        onOk={() => form.submit()}
        okText="儲存"
        cancelText="取消"
      >
        <Form form={form} layout="vertical" onFinish={handleEditSubmit}>
          <Form.Item
            name="name"
            label="姓名"
            rules={[{ max: 100, message: 'Name too long' }]}
          >
            <Input placeholder="使用者姓名" />
          </Form.Item>

          <Form.Item
            name="email"
            label="電子郵件"
            rules={[{ type: 'email', message: 'Invalid email' }]}
          >
            <Input placeholder="user@example.com" />
          </Form.Item>

          <Form.Item
            name="username"
            label="帳號名"
            rules={[
              {
                pattern: /^[a-zA-Z0-9_-]+$/,
                message: 'Username can only contain letters, numbers, underscores, and hyphens',
              },
            ]}
          >
            <Input placeholder="英文數字底線連字符" />
          </Form.Item>

          <Form.Item name="role" label="角色">
            <Select>
              <Option value="boss">老闆</Option>
              <Option value="manager">主管</Option>
              <Option value="employee">員工</Option>
            </Select>
          </Form.Item>

          <Form.Item name="managerId" label="直屬主管">
            <Select allowClear placeholder="選擇直屬主管">
              {potentialManagers
                .filter((u) => u.id !== editingUser?.id) // Can't be own manager
                .map((manager) => (
                  <Option key={manager.id} value={manager.id}>
                    {getUserDisplayName(manager)}
                  </Option>
                ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
